import { CheckCircle2, Clock, AlertTriangle, Ban, Loader2, XCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/cn";

interface Props {
  status: string;
  size?: "sm" | "md";
}

interface BadgeConfig {
  /** ``common.status.*`` 의 키 (label 텍스트는 ``t()`` 로 렌더 시점에 해석). */
  i18nKey: string;
  icon: React.ComponentType<{ className?: string }>;
  cls: string;
  spinning?: boolean;
}

const CONFIG: Record<string, BadgeConfig> = {
  pending: { i18nKey: "status.pending", icon: Clock, cls: "bg-slate-100 text-slate-600" },
  processing: {
    i18nKey: "status.processing",
    icon: Loader2,
    cls: "bg-blue-100 text-blue-700",
    spinning: true,
  },
  completed: {
    i18nKey: "status.completed",
    icon: CheckCircle2,
    cls: "bg-green-100 text-green-700",
  },
  extracted: {
    i18nKey: "status.extracted",
    icon: CheckCircle2,
    cls: "bg-green-100 text-green-700",
  },
  ocr_done: {
    i18nKey: "status.ocr_done",
    icon: CheckCircle2,
    cls: "bg-emerald-50 text-emerald-700",
  },
  rejected: {
    i18nKey: "status.rejected",
    icon: AlertTriangle,
    cls: "bg-amber-100 text-amber-800",
  },
  failed: { i18nKey: "status.failed", icon: XCircle, cls: "bg-red-100 text-red-700" },
  cancelling: {
    i18nKey: "status.cancelling",
    icon: Loader2,
    cls: "bg-amber-100 text-amber-800",
    spinning: true,
  },
  cancelled: {
    i18nKey: "status.cancelled",
    icon: Ban,
    cls: "bg-slate-200 text-slate-600",
  },
};

export default function StatusBadge({ status, size = "sm" }: Props) {
  const { t } = useTranslation();
  const conf = CONFIG[status];
  // 알 수 없는 status — 백엔드 추가 상태 같은 경우 raw 문자열 그대로 노출.
  // CONFIG 에 등록되지 않은 키는 i18n 도 적용하지 않는다 (디버깅 친화적).
  const label = conf ? t(conf.i18nKey) : status;
  const Icon = conf?.icon ?? Clock;
  const cls = conf?.cls ?? "bg-slate-100 text-slate-600";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full font-medium",
        cls,
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
      )}
    >
      <Icon
        className={cn(size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5", conf?.spinning && "animate-spin")}
      />
      {label}
    </span>
  );
}
